import React from 'react';
import { Box, Button, ButtonGroup, Flex, Icon } from '@chakra-ui/react';
import PropTypes from 'prop-types';

/**
 * CommonTabSwitcher — shared tab switcher used across all list pages.
 * Consistent modern style: attached ButtonGroup with icon + label per tab.
 * Active tab: reviveOrange fill. Inactive: white with gray border.
 *
 * `isFullWidth` and `shortLabel` are opt-in (RAT-1926) so the switcher can also
 * sit inside a narrow dashboard card on a phone. Defaults keep every existing
 * list page rendering exactly as before.
 */
const CommonTabSwitcher = ({
  tabs,
  activeIndex,
  onChange,
  mb,
  isFullWidth = false,
  isDisabled = false,
}) => (
  <Flex mb={mb ?? 4} align="center" width={isFullWidth ? '100%' : undefined}>
    <ButtonGroup
      isAttached
      variant="outline"
      size={isFullWidth ? { base: 'xs', md: 'sm' } : 'sm'}
      width={isFullWidth ? '100%' : undefined}
    >
      {tabs.map((tab, index) => {
        const isActive = activeIndex === index;
        return (
          <Button
            key={tab.label}
            onClick={() => onChange(index)}
            isDisabled={isDisabled}
            flex={isFullWidth ? '1' : undefined}
            bg={isActive ? 'reviveOrange' : 'white'}
            color={isActive ? 'white' : 'gray.600'}
            borderColor={isActive ? 'reviveOrange' : 'gray.200'}
            _hover={{
              bg: isActive ? 'reviveOrange' : 'gray.50',
              opacity: isActive ? 0.85 : 1,
            }}
            _active={{ opacity: 0.7 }}
            fontWeight="600"
            px={isFullWidth ? { base: 2, md: 5 } : 5}
            leftIcon={
              tab.icon ? <Icon as={tab.icon} w="13px" h="13px" /> : undefined
            }
          >
            {tab.shortLabel && (
              <Box as="span" display={{ base: 'inline', md: 'none' }}>
                {tab.shortLabel}
              </Box>
            )}
            <Box
              as="span"
              display={
                tab.shortLabel ? { base: 'none', md: 'inline' } : undefined
              }
            >
              {tab.label}
            </Box>
            {tab.badge !== null &&
              tab.badge !== undefined &&
              tab.badge > 0 &&
              isActive && (
                <Flex
                  as="span"
                  ml={2}
                  bg="white"
                  color="reviveOrange"
                  borderRadius="full"
                  fontSize="10px"
                  fontWeight="700"
                  minW="18px"
                  h="18px"
                  align="center"
                  justify="center"
                  px={1}
                >
                  {tab.badge}
                </Flex>
              )}
          </Button>
        );
      })}
    </ButtonGroup>
  </Flex>
);

CommonTabSwitcher.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      shortLabel: PropTypes.string,
      icon: PropTypes.elementType,
      badge: PropTypes.number,
    }),
  ).isRequired,
  activeIndex: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  mb: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isFullWidth: PropTypes.bool,
  isDisabled: PropTypes.bool,
};

export default CommonTabSwitcher;
